'use client';

import { useUserGetByIamId, useUserUpdate, WebshopUserQueries } from '@cldsystems/ws-api-client';
import { useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback } from 'react';
import useCoreConfig from '@/hooks/use-core-config';
import useLogoutOnUserConflict from '@/hooks/use-logout-on-user-conflict';
import { useKeycloak } from '@/providers/keycloak-provider';
import { useDefaultLocale } from '@/providers/root-provider';
import { clearLocaleCookie, getLocaleCookie, setLocaleCookie } from '@/utils/locale-cookie';
import { resolveLocalizedPathname } from '@/utils/locale-href';

type VersionedUserCache = {
  user?: { locale?: string | null } & Record<string, unknown>;
  version?: string | number;
} & Record<string, unknown>;

const userByIamQueryFilter = (iamId: string) =>
  ({
    queryKey: [WebshopUserQueries.GetByIamId, { iamId }],
  }) as const;

/**
 * Window during which `useUserGetByIamId` data is treated as fresh after an optimistic locale
 * write. The route navigation triggered below remounts `Page` (and thus `LocaleFirstBootstrap`),
 * which subscribes a new `useUserGetByIamId` observer. With the default `staleTime: 0` that
 * observer would refetch immediately and race the `PATCH /user` propagation, sometimes coming
 * back with the *old* locale and clobbering the optimistic snapshot — flipping the URL
 * `→ NEW → OLD`. We deliberately do NOT call `invalidateQueries` after the mutation: the
 * optimistic snapshot already carries the user's choice, and the read replica behind
 * `GET /user/by-iam-id` is not guaranteed to be read-your-writes consistent on this window.
 * Stale fields catch up on the next natural refetch once `staleTime` elapses.
 */
const OPTIMISTIC_LOCALE_FRESHNESS_MS = 30_000;

/**
 * Authenticated locale change — owns the full optimistic flow:
 * - `onMutate`: write locale cookie, navigate URL, patch RQ cache for `useUserGetByIamId`
 * - mutation:  PATCH user with the new `locale`
 * - on error:  revert cookie + URL + cache (only when not superseded in the meantime)
 * - on success: bump the cached `version` so subsequent OCC writes don't fail; do NOT
 *   invalidate the query (see `OPTIMISTIC_LOCALE_FRESHNESS_MS`).
 *
 * No-op for guests, when the profile is not loaded yet, or when the locale already matches.
 */
export const usePersistUserLocale = (): ((newLocale: string) => void) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();
  const coreConfig = useCoreConfig();
  const defaultLocale = useDefaultLocale() ?? coreConfig.defaultLanguageCode;
  const { keycloak, isAuthenticated } = useKeycloak();
  const iamId = keycloak?.idTokenParsed?.sub ?? '';
  const { data: userVersioned, error: userVersionedError } = useUserGetByIamId({
    params: { iamId },
    enabled: Boolean(isAuthenticated && iamId),
  });
  useLogoutOnUserConflict(userVersionedError);
  const userUpdate = useUserUpdate();

  return useCallback(
    (newLocale: string) => {
      if (!userVersioned?.user) return;

      const u = userVersioned.user;
      const target = newLocale.toLowerCase();
      if (u.locale?.toLowerCase() === target) return;

      // Read the live query string lazily — `useSearchParams()` would force a
      // CSR bailout for every page that mounts a language selector (build error).
      const suffix = typeof window !== 'undefined' ? window.location.search : '';
      const prevPath = pathname;
      const prevCookie = getLocaleCookie() ?? null;
      const targetPath = resolveLocalizedPathname(newLocale, defaultLocale);

      const variables = {
        params: {
          id: u.id,
          version: String(userVersioned.version),
        },
        payload: {
          firstName: u.firstName,
          lastName: u.lastName,
          email: u.email,
          phone: u.phone,
          locale: newLocale,
          addresses: u.addresses ?? [],
          companies: u.companies ?? [],
        },
      };

      void (async () => {
        await queryClient.cancelQueries(userByIamQueryFilter(iamId));

        const previousCache = queryClient.getQueriesData<VersionedUserCache>(
          userByIamQueryFilter(iamId),
        );

        // Stick the optimistic snapshot across the imminent route remount. Must be set
        // before `setQueriesData` so the new observer sees `dataUpdatedAt + staleTime > now`
        // and skips its refetch-on-mount. Kept in place permanently for this query: the
        // user profile rarely changes from elsewhere, and a 30s freshness window prevents
        // a stale read replica from clobbering the locale right after a write. Explicit
        // `invalidateQueries` calls in other code paths still bypass this and refetch.
        queryClient.setQueryDefaults([WebshopUserQueries.GetByIamId], {
          staleTime: OPTIMISTIC_LOCALE_FRESHNESS_MS,
        });

        try {
          setLocaleCookie(newLocale);
          queryClient.setQueriesData<VersionedUserCache>(userByIamQueryFilter(iamId), old => {
            if (!old?.user) return old;
            return { ...old, user: { ...old.user, locale: newLocale } };
          });
          if (targetPath !== prevPath) {
            router.replace(`${targetPath}${suffix}`);
          }

          await userUpdate.mutateAsync(variables);

          // Bump the cached `version` so the next PATCH on this user (e.g. address /
          // company changes) sends the correct OCC token. We deliberately keep
          // `locale: newLocale` in the cache and skip `invalidateQueries` — refetching
          // here can race the API replica and return the *old* locale, which would
          // flip the URL back via `LocaleFirstBootstrap`.
          queryClient.setQueriesData<VersionedUserCache>(userByIamQueryFilter(iamId), old => {
            if (!old?.user) return old;
            const nextVersion = typeof old.version === 'number' ? old.version + 1 : old.version;
            return {
              ...old,
              version: nextVersion,
              user: { ...old.user, locale: newLocale },
            };
          });
        } catch {
          // Only roll back if the optimistic snapshot is still the current state —
          // e.g. user did not switch again or navigate away in the meantime.
          if (getLocaleCookie()?.toLowerCase() === target) {
            if (prevCookie) setLocaleCookie(prevCookie);
            else clearLocaleCookie();
          }
          if (
            targetPath !== prevPath &&
            typeof window !== 'undefined' &&
            window.location.pathname === targetPath
          ) {
            router.replace(`${prevPath}${suffix}`);
          }
          for (const [key, data] of previousCache) {
            queryClient.setQueryData(key, data);
          }
        }
      })();
    },
    [queryClient, router, pathname, defaultLocale, iamId, userUpdate, userVersioned],
  );
};
